import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, CheckCircle, Circle, ClipboardList } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import { issuesApi, type Issue } from '@/services/issuesApi';
import { planningApi, type PlanningProject } from '@/services/planningApi';
import { githubApi } from '@/services/githubApi';

export default function BacklogPage() {
  const [issues, setIssues] = useState<Issue[]>([]);
  // The org's GitHub Projects boards. These used to come from issuesApi's
  // GET /projects, which collided with the SystemDox projects selector and
  // broke backend deploys. planningApi's /planning/projects runs the same
  // GraphQL query and returns a superset of the same fields (#735).
  const [projects, setProjects] = useState<PlanningProject[]>([]);
  const [repos, setRepos] = useState<string[]>([]);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    githubApi.listRepos().then((repoList) => {
      const connected = repoList.filter((r) => r.connected).map((r) => r.full_name);
      setRepos(connected);
      if (connected.length > 0) setSelectedRepo(connected[0]);
    });
    planningApi.listProjects().then(setProjects).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedRepo) {
      setLoading(false);
      return;
    }
    setLoading(true);
    issuesApi.listIssues(selectedRepo).then(setIssues).finally(() => setLoading(false));
  }, [selectedRepo]);

  const openIssues = issues.filter((i) => i.state === 'open');
  const closedIssues = issues.filter((i) => i.state === 'closed');

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Backlog</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            GitHub issues created from specs.
          </p>
        </div>
        <select
          value={selectedRepo}
          onChange={(e) => setSelectedRepo(e.target.value)}
          className="px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
        >
          {repos.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      {/* Projects */}
      {projects.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
            GitHub Projects
          </h2>
          <div className="flex gap-3 flex-wrap">
            {projects.map((p) => (
              <a
                key={p.id}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-sm hover:border-blue-300 transition-colors"
              >
                <ClipboardList className="w-4 h-4 text-blue-500" />
                <span className="font-medium text-slate-900 dark:text-slate-100">{p.title}</span>
                <ExternalLink className="w-3 h-3 text-slate-400" />
              </a>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <LoadingState size="lg" />
      ) : issues.length === 0 ? (
        <div className="text-center py-12">
          <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-lg font-medium text-slate-600 dark:text-slate-400">No spec-linked issues</p>
          <p className="text-sm text-slate-500 mt-1">
            Create issues from a spec's tasks to see them here.
          </p>
          <Link
            to="/docs/requirements"
            className="inline-flex items-center gap-1 mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Go to Requirements
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Open issues */}
          <div>
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              Open ({openIssues.length})
            </h2>
            <div className="space-y-1">
              {openIssues.map((issue) => (
                <a
                  key={issue.number}
                  href={issue.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 transition-colors"
                >
                  <Circle className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100 flex-1 truncate">
                    #{issue.number} {issue.title}
                  </span>
                  {issue.assignee && (
                    <span className="text-xs text-slate-500 shrink-0">@{issue.assignee}</span>
                  )}
                  <ExternalLink className="w-3 h-3 text-slate-400 shrink-0" />
                </a>
              ))}
            </div>
          </div>

          {/* Closed issues */}
          {closedIssues.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2">
                Closed ({closedIssues.length})
              </h2>
              <div className="space-y-1">
                {closedIssues.map((issue) => (
                  <a
                    key={issue.number}
                    href={issue.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 transition-colors opacity-60"
                  >
                    <CheckCircle className="w-4 h-4 text-purple-500 shrink-0" />
                    <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 truncate line-through">
                      #{issue.number} {issue.title}
                    </span>
                    <ExternalLink className="w-3 h-3 text-slate-400 shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
